import { formatName, getObjectLabels, getQuoteLoanApplication } from '@paintscout/util/builder'
import startCase from 'lodash/startCase'
import type { OptionsDocument, QuoteDocument } from 'paintscout'

/**
 * The financing warnings shown with a Wisetack application. Each one tells someone that money is
 * still owed, so the wording lives in one place: both the legacy WisetackApplication card and the
 * estimateNext WisetackApplicationNext block render exactly these strings.
 *
 * The comparison is against the amount Wisetack would finance now — the balance due plus any
 * Wisetack payment already taken, capped at the merchant's maximum loan amount.
 */
export function getWisetackAlerts({
  quote,
  options,
  maximumLoanAmount,
  inPresentation
}: {
  quote: QuoteDocument
  options: OptionsDocument
  maximumLoanAmount: number
  /** True in the customer-facing document, where the alerts address the customer directly. */
  inPresentation?: boolean
}): string[] {
  const objectLabels = getObjectLabels({ options, quote, invoice: quote?.is_invoice })
  const objectLabel = objectLabels.quote.value.toLowerCase()
  // formatName returns '' for a nameless contact and does not capitalize, so a stored
  // "liam sinni" would read "liam sinni's approved ..." and an empty contact "'s approved ...".
  const contactName = startCase(formatName(quote?.contact)) || 'Your Customer'

  const loanApplication = getQuoteLoanApplication({ contact: quote?.contact, quote })
  const maximumAmount = loanApplication?.maximumAmount
  const approvedAmount = loanApplication?.approvedLoanAmount

  const wisetackPayments = quote?.payments?.filter((payment) => payment.type === 'Wisetack')
  const amountAlreadyFinanced = wisetackPayments?.reduce((acc, payment) => acc + payment.amount, 0) || 0
  const requestedFinanceAmount = Number.parseFloat((quote.totals.balance_due + amountAlreadyFinanced).toFixed(2))
  const totalFinanceAmount = Math.min(requestedFinanceAmount, maximumLoanAmount)

  const alerts: string[] = []

  if (approvedAmount && approvedAmount > totalFinanceAmount) {
    if (inPresentation) {
      alerts.push(
        `Your financing application was previously approved for more than the current balance due on the ${objectLabel}. Please apply again to finance this purchase.`
      )
    } else {
      alerts.push(
        `${contactName}'s financing application was previously approved for more than the current balance due on the ${objectLabel}. They will need to apply for financing for the new balance due amount on the ${objectLabel}.`
      )
    }
  }
  if (approvedAmount && approvedAmount < totalFinanceAmount) {
    if (inPresentation) {
      alerts.push(
        `Your approved financing amount is less than the balance due. You'll need to pay the difference with another payment method.`
      )
    } else {
      alerts.push(
        `${contactName}'s approved financing amount is less than the balance due. They'll need to pay the difference with another payment method.`
      )
    }
  } else if (maximumAmount && maximumAmount < totalFinanceAmount) {
    if (inPresentation) {
      alerts.push(
        `Your maximum qualified amount is less than the balance due. You'll need to pay the difference with another payment method.`
      )
    } else {
      alerts.push(
        `${contactName}'s maximum qualified amount is less than the balance due. They'll need to pay the difference with another payment method.`
      )
    }
  }

  return alerts
}
